import {useMemo} from 'react';
import {Card} from '@atoms';
import {IconButton} from '@molecules';
import {H3} from '@quarks';
import {DayItem} from './DayItem';
import {cn} from '@utils';
import {ConnectionUser, getAppLanguage, getConnectsWorkdaysByUserId, useAppSelector, UserInfo} from '@lib/store';
import {extractWorkdaysForDay} from '../../utils';
import {Workday} from '@lib/store/src/data/timeslots/apiTypes';
import {UserScheduleWorkdayTile} from './UserScheduleWorkdayTile';
import {UserScheduleUnknownWorkdayTile} from './UserScheduleUnknownWorkdayTile';
import {useConnections} from 'src/components/pages/Connections/ConnectionsContext';
import {formatDate} from '@lib/utils';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {dateToLocalDateString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {useTranslation} from 'react-i18next';
import {GROUP_DATA_DATE_RANGE} from '@lib/store/src/data/groups/constants';

type Props = {
  user: UserInfo | ConnectionUser;
};

const currentDate = dateToLocalDateString(new Date());

export const UserSchedule = ({user}: Props) => {
  const {t} = useTranslation();
  const {
    weekLabel,
    showWeekends,
    moveToNextWeek,
    moveToPreviousWeek,
    canMoveToNextWeek,
    canMoveToPreviousWeek,
    daysForSelectedWeek,
    selectedDate,
  } = useConnections();

  const userWorkdays = useAppSelector((state) =>
    getConnectsWorkdaysByUserId(state, {userId: user.id, dateRange: GROUP_DATA_DATE_RANGE}),
  );

  const workdaysForDateMap = useMemo(() => {
    const map: Map<string, Workday[]> = new Map();
    if (!userWorkdays) return map;

    daysForSelectedWeek.forEach((day) => {
      map.set(day, extractWorkdaysForDay(userWorkdays, day));
    });
    return map;
  }, [daysForSelectedWeek, userWorkdays]);

  const selectedDateWorkdays = useMemo(
    () => extractWorkdaysForDay(userWorkdays || [], selectedDate),
    [selectedDate, userWorkdays],
  );

  const appLanguage = useAppSelector(getAppLanguage);
  const date = useMemoDate(selectedDate);

  const selectedLabel =
    selectedDate === currentDate ? t('translation:Today') : formatDate(date, 'EEEE d MMMM', appLanguage);

  return (
    <Card
      className={cn(
        'grid grid-cols-[repeat(5,minmax(0,auto))] sm:grid-cols-5 rounded-none sm:rounded-lg+ justify-between gap-x-2 sm:gap-x-6 md:gap-x-8 px-4 md:px-8 overflow-clip',
        {
          'grid-cols-[repeat(7,minmax(0,auto))] sm:grid-cols-7': showWeekends,
        },
        ['md:rounded-lg+'],
      )}>
      <div className="grid col-span-full md:col-span-full md:max-w-[240px] grid-cols-[auto_1fr_auto] gap-8 md:gap-2 items-center py-4 md:py-8 md:justify-between">
        <IconButton
          onClick={moveToPreviousWeek}
          className="order-1 md:ml-auto md:order-2"
          size="small"
          iconButton={'tertiary'}
          icon={'caretLeft'}
          disabled={!canMoveToPreviousWeek}
        />
        <H3 className="text-nowrap p-0 text-center md:text-left order-2 md:order-1">{weekLabel}</H3>
        <IconButton
          className="order-3"
          onClick={moveToNextWeek}
          size="small"
          iconButton={'tertiary'}
          icon={'caretRight'}
          disabled={!canMoveToNextWeek}
        />
      </div>

      <hr className="col-span-full hidden sm:block -mx-4 md:-mx-8 row-start-2" />

      <div className="grid grid-cols-subgrid col-span-full items-stretch row-start-2 [&>*]:py-4 [&>*]md:py-8 justify-between">
        {daysForSelectedWeek.map((day, index) => (
          <DayItem
            key={index}
            dayKey={day}
            workdaysForDay={workdaysForDateMap.get(day) || []}
          />
        ))}
      </div>

      <hr className="col-span-full -mx-4 md:-mx-8" />

      <div className=" col-span-full items-center py-4 md:py-8 justify-between -mx-4 md:-mx-8 px-4 md:px-8">
        <div className="flex flex-col gap-5 relative">
          <h3>{selectedLabel}</h3>
          <div className="space-y-4">
            {selectedDateWorkdays?.length ? (
              selectedDateWorkdays.map((workday, index) => (
                <UserScheduleWorkdayTile
                  key={workday.id}
                  workday={workday}
                  initialOpen={selectedDateWorkdays.length === 1 || index === 0}
                  user={user}
                />
              ))
            ) : (
              <UserScheduleUnknownWorkdayTile />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
